import Panel from 'Components/Panel';
import Table from 'Components/SimpleTable';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectVerificationProfile } from 'selectors/devices';
import { selectReadingsGroupedByVerification } from 'selectors/deviceVerificationReadings';
import { selectVerificationsByDevice } from 'selectors/deviceVerifications';
import icons from 'utils/ui/icons';
import { renderChecks, renderComments, renderDataInfo, renderStatusBadge, renderYesNo } from './renderUtils';
import { checksNeeded, getDataInfoChips, getTechnicianComments } from './utils';

const columns = [
    { label: '#', dataKey: 'number' },
    { label: 'Date', dataKey: 'date' },
    { label: 'Final Status', dataKey: 'finalStatus', transform: row => renderStatusBadge(row.finalStatus) },
    { label: 'Tech Checks?', dataKey: 'techChecks', transform: row => renderChecks(row.techChecks, row.checksNeeded) },
    { label: 'Live?', dataKey: 'online', transform: row => renderYesNo(row.online) },
    {
        label: 'Data Info',
        dataKey: 'dataInfoChips',
        transform: row => renderDataInfo(row.dataInfoChips)
    },
    {
        label: 'Comments',
        dataKey: 'comments',
        transform: row => renderComments(row.technicianComments, row.finalComments)
    }
];

const Validations = props => {
    const { device, verificationsIndex } = props;
    const { onClean, onSelect } = props;
    const navigate = useNavigate();
    const selectedVerifications = useMemo(() => selectVerificationsByDevice(device.id), [device.id]);
    const verifications = useSelector(selectedVerifications);
    const readingsByVerification = useSelector(selectReadingsGroupedByVerification);
    const { comparison } = useSelector(useMemo(() => selectVerificationProfile(device.id), [device.id]));

    // row ids keep the store index so selection still matches
    const rows = useMemo(() => verifications.map((v, i) => ({
        id: i,
        number: i + 1,
        online: readingsByVerification[v.id]?.at(-1)?.online || false,
        date: v.created_at ? new Date(v.created_at * 1000).toLocaleString() : '',
        dataInfoChips: getDataInfoChips(readingsByVerification[v.id], device.extra.ti_current, comparison),
        technicianComments: getTechnicianComments(readingsByVerification[v.id]),
        techChecks: readingsByVerification[v.id]?.length || 0,
        checksNeeded: checksNeeded(comparison),
        finalStatus: v.final_status || '',
        finalComments: v.final_comments || ''
    })).reverse(), [comparison, device.extra.ti_current, readingsByVerification, verifications]);

    // One open verification per device (API rule) — offering + while one is pending only produces a 400.
    const hasOpenVerification = verifications.some(v => v.state === 'pending');

    const buttons = [
        { icon: icons.sites, tooltip: 'Site verification status', onClick: () => navigate(`/sites/${device.site_id}/verifications`) },
        { icon: icons.hardware, tooltip: 'WiseBox verification status', onClick: () => navigate(`/devices/${device.root_device_id}/verifications`) },
        ...(hasOpenVerification ? [] : [{ icon: icons.plus, tooltip: 'New verification', onClick: onClean }])
    ];

    const loaded = useSelector(state => state.deviceVerifications.loaded);

    return (
        <Panel buttons={buttons}>
            {rows.length > 0 && (
                <Table columns={columns} headerHeight={28} onRowClick={id => onSelect(id)} rowHeight={28} rows={rows} selectedRowId={verificationsIndex}/>
            )}
            {loaded && rows.length === 0 && (
                <div style={{ fontSize: '0.75rem', padding: '10px 0' }}>
                    No verifications for this device. Add one with the + button.
                </div>
            )}
        </Panel>
    );
};

export default Validations;
